// The Player's handle on the <video> inside an /embed frame (public/player/frame.js is the other end).
// It mirrors the frame's video from its messages, sends commands back and fires the same events as a
// <video>, so the Player drives both the same way. Interaction inside the frame comes through as
// extra events: "ready" and "hello" (its state when it starts and when asked), "tap" (the video was
// clicked and toggled), "dblclick", "pointer", "pointerleave", "key" and "levels" (the qualities the
// stream offers).

/**
 * Fired on window while the pointer moves inside a frame. The page gets no mouseleave when the pointer
 * goes from one of its elements straight into a frame, so hover state (the sidebar) listens for this.
 */
export const FRAME_POINTER_EVENT = "sweet:frame-pointer";

export interface FrameEventDetail {
  /** For "tap": whether the click started playback. */
  playing?: boolean;
  /** For "key": KeyboardEvent.key. */
  key?: string;
}

export class FrameMedia extends EventTarget {
  paused = true;
  ended = false;
  duration = NaN;
  /** Heights the stream can switch between, best first; empty when it can't. */
  heights: number[] = [];
  /** Height being played. */
  height = 0;
  autoQuality = true;
  /** Whether the frame's script has answered yet; before that, commands would go nowhere. */
  ready = false;

  private pendingPlay = false;
  private time = 0;
  private bufferedEnd = 0;
  private vol = 1;
  private mute = false;
  private rate = 1;

  constructor(private frame: HTMLIFrameElement) {
    super();
    window.addEventListener("message", this.onMessage);
    // The frame may have loaded before this handle existed (or be reloaded), so ask for its state.
    frame.addEventListener("load", this.hello);
    this.hello();
  }

  dispose() {
    window.removeEventListener("message", this.onMessage);
    this.frame.removeEventListener("load", this.hello);
  }

  get currentTime() {
    return this.time;
  }
  set currentTime(time: number) {
    this.time = time;
    this.post({ cmd: "seek", time });
  }

  get volume() {
    return this.vol;
  }
  set volume(volume: number) {
    this.vol = volume;
    this.post({ cmd: "volume", volume });
  }

  get muted() {
    return this.mute;
  }
  set muted(muted: boolean) {
    this.mute = muted;
    this.post({ cmd: "muted", muted });
  }

  get playbackRate() {
    return this.rate;
  }
  set playbackRate(rate: number) {
    this.rate = rate;
    this.post({ cmd: "rate", rate });
  }

  /** Only the end of what is buffered is mirrored. */
  get buffered() {
    const end = this.bufferedEnd;
    return { length: end > 0 ? 1 : 0, start: () => 0, end: () => end };
  }

  /** Resolves right away: the frame reports the outcome as play/pause events. Waits for the frame if it isn't ready. */
  play(): Promise<void> {
    if (this.ready) this.post({ cmd: "play" });
    else this.pendingPlay = true;
    return Promise.resolve();
  }

  pause() {
    this.post({ cmd: "pause" });
  }

  /** iPhone has no element fullscreen, so the frame's video goes native fullscreen instead. */
  webkitEnterFullscreen() {
    this.post({ cmd: "fullscreen" });
  }

  /**
   * Where the Player's full screen button is (in the frame's own coordinates), or null while hidden: on
   * iPhone the frame lays its own button there, whose tap can start the video's native full screen.
   */
  setFullscreenArea(area: { x: number; y: number; width: number; height: number } | null) {
    this.post({ cmd: "fsarea", area });
  }

  /**
   * On a touch screen once the video has played: a tap on it is reported ("uitap") for the Player to
   * show or hide its controls, rather than toggling playback.
   */
  setUiTaps(on: boolean) {
    this.post({ cmd: "uitaps", on });
  }

  /** 0 for automatic. */
  setQuality(height: number) {
    this.post({ cmd: "quality", height });
  }

  /** The cursor over the frame is the frame's, so it is hidden there along with the controls. */
  setCursorHidden(hidden: boolean) {
    this.post({ cmd: "cursor", hidden });
  }

  private hello = () => this.post({ cmd: "hello" });

  private post(message: object) {
    this.frame.contentWindow?.postMessage(message, "*");
  }

  private onMessage = (e: MessageEvent) => {
    const m = e.data;
    if (e.source !== this.frame.contentWindow || !m || typeof m.type !== "string") return;
    const first = !this.ready;
    this.ready = true;
    if (typeof m.paused === "boolean") {
      this.paused = m.paused;
      this.ended = !!m.ended;
      this.time = Number(m.currentTime) || 0;
      this.duration = Number(m.duration);
      this.bufferedEnd = Number(m.buffered) || 0;
      this.vol = Number(m.volume);
      this.mute = !!m.muted;
      this.rate = Number(m.playbackRate) || 1;
    }
    if (m.type === "levels") {
      this.heights = Array.isArray(m.heights) ? m.heights.filter(Number.isFinite) : [];
      this.height = Number(m.height) || 0;
      this.autoQuality = m.auto !== false;
    }
    const detail: FrameEventDetail = { playing: m.playing === true, key: typeof m.key === "string" ? m.key : undefined };
    this.dispatchEvent(new CustomEvent<FrameEventDetail>(m.type, { detail }));
    // After the Player handled the frame's first message (it may seek to a resume point first).
    if (first && this.pendingPlay) {
      this.pendingPlay = false;
      this.post({ cmd: "play" });
    }
  };
}
